<template>
  <Layout>
    <div class="session-details-container">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <!-- 返回按钮 -->
            <div class="back-button mb-4">
              <button class="btn btn-outline-secondary" @click="$router.go(-1)">
                <i class="fa fa-arrow-left"></i> Back to Sessions
              </button>
            </div>

            <!-- Session详情卡片 -->
            <div class="session-details-card" v-if="session">
              <div class="card">
                <div class="card-header">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <h2 class="session-code">{{ session.code }}</h2>
                      <h3 class="session-title">{{ session.title }}</h3>
                    </div>
                    <div class="session-status">
                      <span 
                        class="status-badge"
                        :class="getStatusClass(session.status)"
                      >
                        {{ session.status }}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-8">
                      <!-- Session描述 -->
                      <div class="section mb-4">
                        <h4 class="section-title">
                          <i class="fa fa-info-circle"></i> Description
                        </h4>
                        <div class="section-content">
                          <p v-if="session.description">{{ session.description }}</p>
                          <p v-else class="text-muted">No description available.</p>
                        </div>
                      </div>

                      <!-- Session目标 -->
                      <div class="section mb-4" v-if="session.objectives">
                        <h4 class="section-title">
                          <i class="fa fa-bullseye"></i> Objectives
                        </h4>
                        <div class="section-content">
                          <p>{{ session.objectives }}</p>
                        </div>
                      </div>

                      <!-- 提交指南 -->
                      <div class="section mb-4" v-if="session.submissionGuidelines">
                        <h4 class="section-title">
                          <i class="fa fa-file-text"></i> Submission Guidelines
                        </h4>
                        <div class="section-content">
                          <p>{{ session.submissionGuidelines }}</p>
                        </div>
                      </div>
                    </div>

                    <div class="col-md-4">
                      <!-- Session信息侧边栏 -->
                      <div class="session-sidebar">
                        <!-- 主要部分 -->
                        <div class="info-item mb-3">
                          <label class="info-label">Primary Section</label>
                          <div class="info-value">
                            <span class="section-badge primary-section">{{ session.primarySection }}</span>
                          </div>
                        </div>

                        <!-- 次要部分 -->
                        <div class="info-item mb-3" v-if="session.secondarySection">
                          <label class="info-label">Secondary Section</label>
                          <div class="info-value">
                            <span class="section-badge secondary-section">{{ session.secondarySection }}</span>
                          </div>
                        </div>

                        <!-- 会议类型 -->
                        <div class="info-item mb-3" v-if="session.sessionType">
                          <label class="info-label">Session Type</label>
                          <div class="info-value">{{ session.sessionType }}</div>
                        </div>

                        <!-- 创建日期 -->
                        <div class="info-item mb-3" v-if="session.createdDate">
                          <label class="info-label">Created Date</label>
                          <div class="info-value">{{ formatDate(session.createdDate) }}</div>
                        </div>

                        <!-- 最后更新 -->
                        <div class="info-item mb-3" v-if="session.lastUpdated">
                          <label class="info-label">Last Updated</label>
                          <div class="info-value">{{ formatDate(session.lastUpdated) }}</div>
                        </div>

                        <!-- 操作按钮 -->
                        <div class="action-buttons mt-4">
                          <button class="btn btn-primary btn-block mb-2" @click="submitAbstract">
                            <i class="fa fa-plus"></i> Submit Abstract
                          </button>
                          <button class="btn btn-outline-secondary btn-block" @click="contactConveners">
                            <i class="fa fa-envelope"></i> Contact Conveners
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Conveners部分 -->
                  <div class="conveners-section mt-4">
                    <h4 class="section-title">
                      <i class="fa fa-users"></i> Conveners
                    </h4>
                    <div class="conveners-list">
                      <div 
                        v-for="(convener, index) in session.conveners" 
                        :key="index"
                        class="convener-card"
                      >
                        <div class="convener-header">
                          <span v-if="index === 0" class="lead-convener-badge">Lead Convener</span>
                          <span v-else class="convener-badge">Convener</span>
                        </div>
                        <div class="convener-info">
                          <h5 class="convener-name">{{ convener.name }}</h5>
                          <p class="convener-affiliation">{{ convener.affiliation }}</p>
                          <p v-if="convener.email" class="convener-email">
                            <i class="fa fa-envelope"></i> {{ convener.email }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 加载状态 -->
            <div v-else-if="loading" class="loading-container">
              <div class="text-center">
                <i class="fa fa-spinner fa-spin fa-3x text-primary"></i>
                <p class="mt-3">Loading session details...</p>
              </div>
            </div>

            <!-- 错误状态 -->
            <div v-else class="error-container">
              <div class="text-center">
                <i class="fa fa-exclamation-triangle fa-3x text-warning"></i>
                <h3 class="mt-3">Session Not Found</h3>
                <p>The session you're looking for doesn't exist or has been removed.</p>
                <button class="btn btn-primary" @click="$router.push('/sessions')">
                  Back to Sessions
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue'
import {apiService} from '@/config/api'

export default {
  name: 'SessionDetails',
  components: {
    Layout
  },
  data() {
    return {
      session: null,
      loading: true
    }
  },
  mounted() {
    // this.loadSessionDetails()
    this.getSessionDetails()
  },
  methods: {
    async getSessionDetails() {
      try {
        const response = await apiService.sessions.getSessionDetails({ id: this.$route.params.id })
        this.session = response.data.data
      } catch (error) {
        console.error('获取会议详情失败:', error)
        // 如果API调用失败，使用模拟数据
        await this.loadSessionDetails()
      } finally {
        this.loading = false
      }
    },
    async loadSessionDetails() {
      const sessionId = this.$route.params.id
      
      try {
        // 模拟API调用
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // 模拟数据
        this.session = {
          id: sessionId,
          code: 'AS02',
          title: 'Asian Monsoon, Extremes and Climate Change',
          description: 'This session focuses on the Asian monsoon system, its extremes, and their relationship with climate change. We welcome contributions on monsoon dynamics, extreme weather events, climate modeling, and observational studies.',
          objectives: 'To advance understanding of Asian monsoon variability, extreme events, and their climate change implications through interdisciplinary research.',
          submissionGuidelines: 'Abstracts should be submitted through the conference submission system. Please include clear objectives, methodology, and expected outcomes.',
          primarySection: 'AS',
          secondarySection: 'OS',
          sessionType: 'Oral and Poster',
          status: 'Active',
          createdDate: '2024-12-01',
          lastUpdated: '2025-01-15',
          conveners: [
            {
              name: 'Dr Ramesh Kripalani',
              affiliation: 'Retired Senior Scientist, Indian Institute of Tropical Meteorology, India',
              email: 'rkripalani@example.com'
            },
            {
              name: 'Prof Kyung-Ja Ha',
              affiliation: 'Pusan National University, Korea, South',
              email: 'kjha@pusan.ac.kr'
            },
            {
              name: 'Prof Jun Matsumoto',
              affiliation: 'Tokyo Metropolitan University, Japan',
              email: 'matsumoto@tmu.ac.jp'
            },
            {
              name: 'Prof Jai-Ho Oh',
              affiliation: 'Nano C&W, Korea, South',
              email: 'jaiho.oh@example.com'
            },
            {
              name: 'Prof Renguang Wu',
              affiliation: 'Zhejiang University, China',
              email: 'renguang@zju.edu.cn'
            }
          ]
        }
      } catch (error) {
        console.error('Failed to load session details:', error)
      } finally {
        this.loading = false
      }
    },
    getStatusClass(status) {
      switch (status) {
        case 'Active': return 'status-active'
        case 'Withdrawn': return 'status-withdrawn'
        case 'Pending': return 'status-pending'
        default: return 'status-default'
      }
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    submitAbstract() {
      this.$router.push('/my-abstracts')
    },
    contactConveners() {
      // 实现联系Conveners的逻辑
      alert('Contact conveners functionality will be implemented here.')
    }
  }
}
</script>

<style scoped>
.session-details-container {
  padding: 20px 0;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.back-button {
  margin-bottom: 20px;
}

.session-details-card .card {
  border: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
}

.session-details-card .card-header {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  border-bottom: none;
  padding: 30px;
}

.session-code {
  font-size: 2.5rem;
  font-weight: bold;
  margin: 0;
  color: white;
}

.session-title {
  font-size: 1.5rem;
  font-weight: 400;
  margin: 10px 0 0 0;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.4;
}

.session-status {
  text-align: right;
}

.status-badge {
  display: inline-block;
  padding: 8px 16px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
}

.status-active {
  background-color: #28a745;
  color: white;
}

.status-withdrawn {
  background-color: #dc3545;
  color: white;
}

.status-pending {
  background-color: #ffc107;
  color: #212529;
}

.status-default {
  background-color: #6c757d;
  color: white;
}

.card-body {
  padding: 30px;
}

.section {
  margin-bottom: 30px;
}

.section-title {
  color: #333;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 15px;
  border-bottom: 2px solid #3498DB;
  padding-bottom: 8px;
}

.section-title i {
  color: #3498DB;
  margin-right: 8px;
}

.section-content {
  color: #666;
  line-height: 1.6;
}

.session-sidebar {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border-left: 4px solid #3498DB;
}

.info-item {
  margin-bottom: 20px;
}

.info-label {
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  color: #666;
  font-size: 16px;
}

.section-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  text-align: center;
  min-width: 40px;
}

.primary-section {
  background-color: #3498DB;
  color: white;
}

.secondary-section {
  background-color: #6c757d;
  color: white;
}

.action-buttons {
  margin-top: 30px;
}

.conveners-section {
  border-top: 1px solid #dee2e6;
  padding-top: 30px;
}

.conveners-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.convener-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.3s ease;
}

.convener-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.convener-header {
  margin-bottom: 15px;
}

.lead-convener-badge {
  background-color: #dc3545;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
}

.convener-badge {
  background-color: #6c757d;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
}

.convener-name {
  color: #333;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
}

.convener-affiliation {
  color: #666;
  font-size: 14px;
  margin-bottom: 8px;
  line-height: 1.4;
}

.convener-email {
  color: #3498DB;
  font-size: 14px;
  margin-bottom: 0;
}

.convener-email i {
  margin-right: 5px;
}

.loading-container,
.error-container {
  background: white;
  padding: 60px 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

@media (max-width: 768px) {
  .session-code {
    font-size: 2rem;
  }
  
  .session-title {
    font-size: 1.25rem;
  }
  
  .conveners-list {
    grid-template-columns: 1fr;
  }
  
  .session-sidebar {
    margin-top: 20px;
  }
}
</style> 